import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import UserPatternSettingUpdate from "./_111_user_pattern_setting_update.js";
import { logEvent } from "./eventLogger.js";
import { getCachedPatternData, setCachedPatternData } from "./patterns/cacheHelper.js";
import { PATTERN_CACHE_KEYS, PATTERN_ENDPOINTS } from "./patterns/cacheConfig.js";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip as ChartTooltip,
    Legend,
    Filler,
} from "chart.js";
import { Chart } from "react-chartjs-2";
import { useDateRange } from "./dateRangeContext.js";
import { formatDateMMDD } from "./patterns/helpers.js";
import "./styles.css";

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    ChartTooltip,
    Legend,
    Filler
);

const MODULE_NAME = "91_pattern_low_price_reco";
const API_ENDPOINT = PATTERN_ENDPOINTS.LOW_PRICE;

// --- Gear Icon Unicode ---
const GearIcon = ({ size = 24, color = "#fff" }) => (
    <span
        style={{
            fontSize: size,
            color,
            verticalAlign: "middle",
            cursor: "pointer",
            userSelect: "none",
            lineHeight: 1,
            display: "inline-block",
        }}
        title="Settings"
        aria-label="Settings"
    >
        &#9881;
    </span>
);

// --- Table Styles ---
const tableStyle = {
    width: "100%",
    borderCollapse: "collapse",
    marginTop: 16,
    background: "#222",
};

const thStyle = {
    padding: "8px 12px",
    fontSize: 13,
    textAlign: "left",
    borderBottom: "1px solid #555",
    backgroundColor: "#333",
    fontWeight: 600,
    color: "#fff",
};

const tdStyle = {
    padding: "8px 12px",
    borderBottom: "1px solid #444",
    fontSize: 13,
    color: "#ccc",
};

const Pattern_low_price_reco = ({ userId: propUserId }) => {
    const { windowDays } = useDateRange();
    
    const storedId = sessionStorage.getItem("user_id");
    const userId = propUserId || (storedId && !isNaN(Number(storedId)) ? Number(storedId) : 1);

    const [date, setDate] = useState("");
    const [tooltip, setTooltip] = useState("");
    const [lowPricePattern, setLowPricePattern] = useState(null);
    const [totalPL, setTotalPL] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showDetails, setShowDetails] = useState(false);
    const [chartMode, setChartMode] = useState("cumulative");
    const [timeEvolution, setTimeEvolution] = useState([]);
    const [showSettings, setShowSettings] = useState(false);
    const [patternId, setPatternId] = useState(null);
    const [patternConfig, setPatternConfig] = useState(null);
    const [fromCache, setFromCache] = useState(false);
    const [showTable, setShowTable] = useState(false);
    const [lowPriceMatches, setLowPriceMatches] = useState([]);

    // Helper to parse windowDays to a valid lookback number
    function getLookbackDays(val) {
        if (!val) return 30;
        if (!isNaN(Number(val))) return Number(val);
        if (val === "YTD") return 365;
        return 30;
    }

    // ✅ UPDATED: Fetch data with async database cache using numeric pattern_id and userKey for decryption
    const fetchPatternData = async (lookback) => {
        setLoading(true);
        
        try {
            // 🔓 NEW: Retrieve userKey from sessionStorage for decryption
            const userKey = sessionStorage.getItem("userKey");
            const fetchParams = { 
                lookback_days: lookback 
            };

            // Inject userKey if Privacy Mode is active
            if (userKey) {
                fetchParams.userKey = userKey;
            }

            const res = await axios.get(
                `${process.env.REACT_APP_API_URL}${API_ENDPOINT}`,
                {
                    params: fetchParams,
                    withCredentials: true,
                }
            );
            
            // ✅ DEBUG: Log entire response
            console.log("[91_pattern_low_price_reco] Full API Response:", res.data);
            console.log("[91_pattern_low_price_reco] low_price_matches:", res.data.low_price_matches);
            console.log("[91_pattern_low_price_reco] low_price_matches length:", res.data.low_price_matches?.length);
            
            setDate(res.data.date);
            setTooltip(res.data.tooltip);

            const pattern = res.data.low_price_pattern || {};
            setLowPricePattern(pattern);
            setTimeEvolution(pattern.time_evolution || []);
            
            // ✅ DEBUG: Check what we're setting
            // low_price_matches is INSIDE the pattern object returned by backend
            const matches = pattern.low_price_matches || [];
            console.log("[91_pattern_low_price_reco] Setting lowPriceMatches to:", matches);
            setLowPriceMatches(matches);
            
            setTotalPL(
                typeof pattern.total_pl === "number"
                    ? pattern.total_pl
                    : null
            );

            // ✅ UPDATED: Extract numeric pattern_id from API response
            const numericPatternId = pattern.pattern_id;
            setPatternId(numericPatternId);
            setPatternConfig(pattern.config || {});

            // ✅ UPDATED: Use numeric pattern_id as cache key
            if (numericPatternId) {
                await setCachedPatternData(
                    `pattern_${numericPatternId}`,
                    lookback,
                    res.data
                );
            }

            console.log("Pattern ID:", numericPatternId, "Config:", pattern.config);
            setFromCache(false);
        } catch (err) {
            console.error("Error fetching pattern data:", err);
            
            // ✅ Try to load from cache on error
            if (patternId) {
                const cachedData = await getCachedPatternData(
                    `pattern_${patternId}`,
                    lookback
                );
                if (cachedData) {
                    setDate(cachedData.date);
                    setTooltip(cachedData.tooltip);
                    const pattern = cachedData.low_price_pattern || {};
                    setLowPricePattern(pattern);
                    setTimeEvolution(pattern.time_evolution || []);
                    setLowPriceMatches(pattern.low_price_matches || []);  // ✅ Changed from cachedData
                    setTotalPL(typeof pattern.total_pl === "number" ? pattern.total_pl : null);
                    setPatternConfig(pattern.config || {});
                    setFromCache(true);
                    setLoading(false);
                    return;
                }
            }
            
            setTooltip("Error fetching data.");
            setLowPricePattern(null);
            setTimeEvolution([]);
            setLowPriceMatches([]);
            setTotalPL(null);
        }
        
        setLoading(false);
    };

    useEffect(() => {
        const lookback = getLookbackDays(windowDays);
        if (lookback) {
            fetchPatternData(lookback);
        }
        // eslint-disable-next-line
    }, [windowDays]);

    const calculateRollingAverage = (data, key, window = 14) => {
        return data.map((point, idx) => {
            const start = Math.max(0, idx - window + 1);
            const slice = data.slice(start, idx + 1);
            const avg = slice.reduce((sum, p) => sum + p[key], 0) / slice.length;
            return avg;
        });
    };

    const getYearLabels = (data) => {
        const yearLabels = [];
        let lastYear = null;
        data.forEach((d, idx) => {
            const year = new Date(d.date).getFullYear();
            if (year !== lastYear) {
                yearLabels.push({ year, idx });
                lastYear = year;
            }
        });
        return yearLabels;
    };

    const chartLabels = timeEvolution.map(d => formatDateMMDD(d.date));
    const yearLabels = getYearLabels(timeEvolution);

    const datasetsByMode = () => {
        if (!timeEvolution || timeEvolution.length === 0) {
            return { labels: [], datasets: [] };
        }

        const labels = timeEvolution.map(d => formatDateMMDD(d.date));
        const frequency = timeEvolution.map(d => d.frequency);
        const cumulativePL = timeEvolution.map(d => d.cumulative_pl);
        const winRate = timeEvolution.map(d => d.cumulative_win_rate);
        const wins = timeEvolution.map(d => d.daily_wins || 0);
        const losses = frequency.map((f, i) => Math.max(0, f - wins[i]));

        let datasets = [];

        if (chartMode === "cumulative") {
            datasets = [
                {
                    type: "line",
                    label: "Cumulative P/L",
                    data: cumulativePL,
                    borderColor: "#00FFFF",
                    backgroundColor: "rgba(0, 255, 255, 0.1)",
                    borderWidth: 3,
                    pointRadius: 4,
                    pointBackgroundColor: "#00FFFF",
                    fill: true,
                    tension: 0.3,
                    yAxisID: "y",
                },
                {
                    type: "line",
                    label: "Win Rate %",
                    data: winRate,
                    borderColor: "#FFD700",
                    backgroundColor: "rgba(255, 215, 0, 0.1)",
                    borderWidth: 2,
                    pointRadius: 3,
                    pointBackgroundColor: "#FFD700",
                    fill: true,
                    tension: 0.3,
                    yAxisID: "y1",
                },
                {
                    type: "bar",
                    label: "Wins",
                    data: wins,
                    backgroundColor: "rgba(76, 175, 80, 0.6)",
                    borderColor: "#4CAF50",
                    borderWidth: 1,
                    yAxisID: "y2",
                    stack: "frequency",
                },
                {
                    type: "bar",
                    label: "Losses",
                    data: losses,
                    backgroundColor: "rgba(244, 67, 54, 0.6)",
                    borderColor: "#F44336",
                    borderWidth: 1,
                    yAxisID: "y2",
                    stack: "frequency",
                },
            ];
        } else if (chartMode === "rolling") {
            const rollingPL = calculateRollingAverage(timeEvolution, "cumulative_pl", 14);
            const rollingWinRate = calculateRollingAverage(timeEvolution, "cumulative_win_rate", 14);

            datasets = [
                {
                    type: "line",
                    label: "14-Day Rolling Avg P/L",
                    data: rollingPL,
                    borderColor: "#FF6B9D",
                    backgroundColor: "rgba(255, 107, 157, 0.1)",
                    borderWidth: 3,
                    pointRadius: 4,
                    pointBackgroundColor: "#FF6B9D",
                    fill: true,
                    tension: 0.3,
                    yAxisID: "y",
                },
                {
                    type: "line",
                    label: "14-Day Rolling Avg Win Rate %",
                    data: rollingWinRate,
                    borderColor: "#FFA500",
                    backgroundColor: "rgba(255, 165, 0, 0.1)",
                    borderWidth: 2,
                    pointRadius: 3,
                    pointBackgroundColor: "#FFA500",
                    fill: true,
                    tension: 0.3,
                    yAxisID: "y1",
                },
                {
                    type: "bar",
                    label: "Wins",
                    data: wins,
                    backgroundColor: "rgba(76, 175, 80, 0.6)",
                    borderColor: "#4CAF50",
                    borderWidth: 1,
                    yAxisID: "y2",
                },
            ];
        } else if (chartMode === "tradeByTrade") {
            datasets = [
                {
                    type: "scatter",
                    label: "Trade P/L",
                    data: timeEvolution.map((d, idx) => ({
                        x: idx,
                        y: d.daily_pl,
                    })),
                    backgroundColor: timeEvolution.map(d => d.daily_pl >= 0 ? "rgba(76, 175, 80, 0.6)" : "rgba(244, 67, 54, 0.6)"),
                    pointRadius: 6,
                    yAxisID: "y",
                },
                {
                    type: "line",
                    label: "Cumulative P/L",
                    data: cumulativePL,
                    borderColor: "#00FFFF",
                    borderWidth: 2,
                    fill: false,
                    tension: 0.3,
                    yAxisID: "y",
                    pointRadius: 0,
                },
                {
                    type: "bar",
                    label: "Frequency",
                    data: frequency,
                    backgroundColor: "rgba(76, 175, 80, 0.3)",
                    borderColor: "#4CAF50",
                    borderWidth: 1,
                    yAxisID: "y2",
                },
            ];
        }

        return {
            labels,
            datasets,
        };
    };

    const chartData = datasetsByMode();
    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: true, position: "top" },
            title: { display: true, text: "Low Price Pattern Time Evolution" },
        },
        scales: {
            y: {
                type: "linear",
                position: "left",
                title: { display: true, text: "P/L ($)" },
                ticks: { color: "#fff" },
                grid: { color: "#444" },
            },
            y1: {
                type: "linear",
                position: "right",
                title: { display: true, text: "Win Rate (%)" },
                ticks: { color: "#fff" },
                grid: { drawOnChartArea: false },
            },
            y2: {
                type: "linear",
                position: "right",
                title: { display: true, text: "Frequency" },
                ticks: { color: "#fff" },
                grid: { drawOnChartArea: false },
                offset: true,
            },
            x: {
                ticks: { color: "#fff" },
                grid: { color: "#444" },
            },
        },
    };

    // --- Settings Modal ---
    const renderSettingsModal = () => {
        if (!showSettings || !patternId) return null;

        return (
            <>
                <div
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100vw",
                        height: "100vh",
                        background: "rgba(24,26,37,0.5)",
                        backdropFilter: "blur(6px)",
                        zIndex: 9998
                    }}
                    onClick={() => setShowSettings(false)}
                />
                <div
                    style={{
                        position: "fixed",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        zIndex: 9999,
                        minWidth: 600,
                        minHeight: 420,
                        background: "#181A25",
                        borderRadius: 12,
                        boxShadow: "0 2px 8px rgba(0,255,255,0.08)",
                        padding: 0
                    }}
                >
                    <UserPatternSettingUpdate
                        patternId={patternId}
                        countThreshold={patternConfig?.count_threshold}
                        thresholdDefinition={patternConfig?.threshold_definition}
                    />
                    <button
                        onClick={() => setShowSettings(false)}
                        style={{
                            position: "absolute",
                            top: 12,
                            right: 18,
                            background: "none",
                            border: "none",
                            color: "#fff",
                            fontSize: 22,
                            cursor: "pointer"
                        }}
                        title="Close"
                    >✕</button>
                </div>
            </>
        );
    };

    // --- Data Table ---
    const renderDataTable = () => {
        console.log("[91_pattern_low_price_reco] renderDataTable called, lowPriceMatches:", lowPriceMatches);
        
        if (!lowPriceMatches || lowPriceMatches.length === 0) {
            return <div style={{ color: "#888", padding: 16 }}>No trade data available.</div>;
        }

        return (
            <div style={{ marginTop: 16 }}>
                <div style={{ marginBottom: 12 }}>
                    <button
                        onClick={() => setShowTable(!showTable)}
                        style={{
                            padding: "8px 16px",
                            borderRadius: 6,
                            border: "1px solid #888",
                            background: showTable ? "#1976d2" : "#333",
                            color: "#fff",
                            fontWeight: 500,
                            cursor: "pointer",
                            transition: "background 0.2s",
                            fontSize: "0.98em",
                        }}
                    >
                        {showTable ? "Hide Trade Details" : "Show Trade Details"} ({lowPriceMatches.length} trades)
                    </button>
                </div>

                {showTable && (
                    <div style={{ overflowX: "auto" }}>
                        {lowPriceMatches.length >= 500 && (
                            <div style={{ 
                                color: "#FFD700", 
                                fontSize: "0.9em", 
                                marginBottom: 8,
                                padding: "6px 12px",
                                background: "rgba(255, 215, 0, 0.1)",
                                borderRadius: 4
                            }}>
                                ⚠️ Showing first 500 records of {lowPriceMatches.length} total matches
                            </div>
                        )}
                        <table style={tableStyle}>
                            <thead>
                                <tr>
                                    <th style={thStyle}>Symbol</th>
                                    <th style={thStyle}>Trade Date</th>
                                    <th style={thStyle}>Avg Price</th>
                                    <th style={thStyle}>P/L</th>
                                    <th style={thStyle}>Quantity</th>
                                    <th style={thStyle}>View Tags</th>
                                </tr>
                            </thead>
                            <tbody>
                                {lowPriceMatches.map((trade, idx) => {
                                    const tradeDate = trade.trade_date;
                                    const isoDate = /^\d{4}-\d{2}-\d{2}$/.test(tradeDate)
                                        ? tradeDate
                                        : `${tradeDate.split("/").reverse().join("-")}`;

                                    return (
                                        <tr key={idx}>
                                            <td style={tdStyle}>
                                                {trade.symbol}
                                            </td>
                                            <td style={tdStyle}>
                                                {formatDateMMDD(trade.trade_date)}
                                            </td>
                                            <td style={tdStyle}>
                                                ${typeof trade.entry_price === "number" ? trade.entry_price.toFixed(2) : "N/A"}
                                            </td>
                                            <td style={{
                                                ...tdStyle,
                                                color: trade.pl >= 0 ? "#4caf50" : "#e53935",
                                                fontWeight: 600,
                                            }}>
                                                ${trade.pl.toLocaleString(undefined, {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2,
                                                })}
                                            </td>
                                            <td style={tdStyle}>
                                                {trade.quantity}
                                            </td>
                                            <td style={tdStyle}>
                                                <Link
                                                    to={`/pg_details?date=${isoDate}&symbol=${trade.symbol}`}
                                                    style={{
                                                        color: "#0077cc",
                                                        textDecoration: "underline",
                                                        cursor: "pointer",
                                                        fontSize: "0.98em"
                                                    }}
                                                    title={`View tags for ${trade.symbol} on ${formatDateMMDD(trade.trade_date)}`}
                                                    onClick={() => {
                                                        sessionStorage.setItem("navigationDate", isoDate);
                                                        sessionStorage.setItem("navigationSymbol", trade.symbol);
                                                        logEvent({
                                                            event_type: "button_click",
                                                            user_id: userId,
                                                            module_name: MODULE_NAME,
                                                            subcomponent: "View Tags Link",
                                                            screen: window.location.pathname,
                                                            metadata: {
                                                                symbol: trade.symbol,
                                                                trade_date: isoDate,
                                                                button_link_value: "Tag details"
                                                            }
                                                        });
                                                    }}
                                                >
                                                    Tag details
                                                </Link>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                            <tfoot>
                                <tr>
                                    <td colSpan="3" style={{
                                        ...tdStyle,
                                        fontWeight: 600,
                                        backgroundColor: "#2a2d38",
                                        textAlign: "right"
                                    }}>
                                        Total:
                                    </td>
                                    <td style={{
                                        ...tdStyle,
                                        fontWeight: 600,
                                        backgroundColor: "#2a2d38",
                                        color: lowPriceMatches.reduce((sum, t) => sum + t.pl, 0) >= 0 ? "#4caf50" : "#e53935",
                                    }}>
                                        ${lowPriceMatches.reduce((sum, t) => sum + t.pl, 0).toLocaleString(undefined, {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                        })}
                                    </td>
                                    <td style={{
                                        ...tdStyle,
                                        fontWeight: 600,
                                        backgroundColor: "#2a2d38",
                                    }}>
                                        {lowPriceMatches.reduce((sum, t) => sum + (t.quantity || 0), 0)}
                                    </td>
                                    <td style={{
                                        ...tdStyle,
                                        fontWeight: 600,
                                        backgroundColor: "#2a2d38",
                                    }}>
                                    </td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                )}
            </div>
        );
    };

    const renderPatternSummary = () => {
        if (!lowPricePattern) {
            return <span>No trades matched this pattern in selected range.</span>;
        }

        const {
            pattern_label,
            pattern_meaning,
            pattern_matches,
            pattern_price_threshold,
            lookback_days,
        } = lowPricePattern;

        return (
            <div>
                {typeof totalPL === "number" && (
                    <div style={{ marginBottom: 8, color: "#e0b400", fontSize: "1.1em" }}>
                        <b>P/L:</b>{" "}
                        <span style={{ color: totalPL >= 0 ? "#4caf50" : "#e53935" }}>
                            ${totalPL.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                    </div>
                )}
                {pattern_label && (
                    <div style={{ marginBottom: 6, color: "#1976d2", fontWeight: 600 }}>
                        {pattern_label}: {pattern_meaning}
                    </div>
                )}
                <div style={{ color: "#555", fontSize: "0.98em", marginBottom: 6 }}>
                    Pattern matches: {pattern_matches}
                    {typeof pattern_price_threshold === "number" && (
                        <>
                            <br />
                            Threshold: ${pattern_price_threshold.toFixed(2)}
                        </>
                    )}
                </div>
                <div style={{ margin: "8px 0" }} data-analytics={MODULE_NAME}>
                    <a
                        href="#"
                        style={{
                            color: "#4fa3f7",
                            textDecoration: "underline",
                            cursor: "pointer",
                            fontSize: "0.97em",
                        }}
                        onClick={e => {
                            e.preventDefault();
                            const newVal = !showDetails;
                            logEvent({
                                event_type: "link_click",
                                user_id: userId,
                                module_name: MODULE_NAME,
                                subcomponent: "Details Toggle",
                                screen: window.location.pathname,
                                metadata: { 
                                    button_link_value: newVal ? "Hide details" : "See more",
                                    action: newVal ? "expanded" : "collapsed"
                                }
                            });
                            setShowDetails(newVal);
                        }}
                    >
                        {showDetails ? "Hide details" : "See more"}
                    </a>
                    {showDetails && (
                        <div style={{ marginTop: 6, color: "#aaa", fontSize: "0.97em" }}>
                            <div>
                                <b>Lookback:</b> {lowPricePattern?.lookback_days} days
                            </div>
                            <div>
                                <b>Date:</b> {date}
                            </div>
                            {fromCache && (
                                <div style={{ marginTop: 6, fontSize: "0.9em", color: "#888" }}>
                                    📦 Data loaded from database cache (4 hour TTL)
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const renderChartToggles = () => (
        <div style={{ display: "flex", gap: 10, marginBottom: 8 }}>
            {["cumulative", "rolling", "tradeByTrade"].map((mode) => {
                const buttonLabels = {
                    cumulative: "Cumulative",
                    rolling: "Rolling Avg",
                    tradeByTrade: "Trade Scatter"
                };
                const buttonLabel = buttonLabels[mode];
                const subcomponentLabel = mode === "cumulative" 
                    ? "Cumulative Chart" 
                    : mode === "rolling" 
                    ? "Rolling Avg Chart" 
                    : "Trade Scatter Chart";

                return (
                    <div key={mode} data-analytics={MODULE_NAME}>
                        <button
                            className={`btn ${chartMode === mode ? "btn-selected" : ""}`}
                            onClick={() => {
                                logEvent({
                                    event_type: "button_click",
                                    user_id: userId,
                                    module_name: MODULE_NAME,
                                    subcomponent: subcomponentLabel,
                                    screen: window.location.pathname,
                                    metadata: {
                                        button_link_value: buttonLabel
                                    }
                                });
                                setChartMode(mode);
                            }}
                            style={{
                                padding: "6px 14px",
                                borderRadius: 6,
                                border: "1px solid #888",
                                background: chartMode === mode ? "#1976d2" : "#f5f5f5",
                                color: chartMode === mode ? "#fff" : "#222",
                                fontWeight: 500,
                                cursor: "pointer",
                                transition: "background 0.2s",
                                fontSize: "0.98em",
                            }}
                        >
                            {buttonLabel}
                        </button>
                    </div>
                );
            })}
        </div>
    );

    const hasPatternMatches = lowPricePattern?.pattern_matches > 0;

    return (
        <div style={{ width: "100%", background: "none", borderRadius: 0, padding: 0, margin: 0, maxWidth: "100%", position: "relative" }}>
            <h3
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    fontSize: "1.25em",
                    margin: "0 0 8px 0",
                    color: "#fff",
                }}
            >
                {lowPricePattern?.pattern_name || "Low Price Pattern"}{" "}
                {lowPricePattern?.tooltip && (
                    <span
                        className="tooltip-icon"
                        style={{
                            marginLeft: 6,
                            verticalAlign: "middle",
                            cursor: "pointer",
                            fontSize: "1.1em",
                        }}
                        title={lowPricePattern.tooltip}
                    >
                        🛈
                    </span>
                )}
                <span
                    style={{ marginLeft: 8 }}
                    title="Edit pattern settings"
                    onClick={() => {
                        logEvent({
                            event_type: "button_click",
                            user_id: userId,
                            module_name: MODULE_NAME,
                            subcomponent: "Settings Gear",
                            screen: window.location.pathname,
                            metadata: { button_link_value: "Settings Gear" }
                        });
                        setShowSettings(true);
                    }}
                >
                    <GearIcon size={24} color="#fff" />
                </span>
            </h3>
            {renderSettingsModal()}
            <div style={{ minHeight: 32, fontSize: "1.05em", marginBottom: 16 }}>
                {loading ? <span>Loading...</span> : renderPatternSummary()}
            </div>
            {hasPatternMatches && renderChartToggles()}
            {hasPatternMatches && timeEvolution && timeEvolution.length > 0 ? (
                <div style={{ position: "relative", width: "100%" }}>
                    <div style={{ marginTop: 8, marginBottom: 0, width: "100%", minHeight: 320, maxWidth: "100%" }}>
                        {chartData.datasets.length > 0 ? (
                            <Chart
                                type="bar"
                                data={{ ...chartData, labels: chartLabels }}
                                options={({ ...chartOptions,
                                    responsive: true,
                                    maintainAspectRatio: false,
                                })}
                            />
                        ) : (
                            <div style={{ color: "#888", padding: "20px", textAlign: "center" }}>
                                No chart data available for this period.
                            </div>
                        )}
                    </div>
                    {yearLabels.length > 1 && (
                        <div
                            style={{
                                pointerEvents: "none",
                                position: "absolute",
                                left: 0,
                                right: 0,
                                bottom: "-20px",
                                height: 24,
                                display: "flex",
                                alignItems: "flex-start",
                                justifyContent: "flex-start",
                                zIndex: 2,
                                width: "100%",
                                fontWeight: 600,
                                fontSize: "1em",
                                color: "#222",
                                userSelect: "none",
                                textShadow: "0 1px 2px #fff, 0 0 2px #fff"
                            }}
                        >
                            {yearLabels.map((yl, i) => {
                                const nextIdx = yearLabels[i + 1]?.idx ?? chartLabels.length;
                                const colSpan = nextIdx - yl.idx;
                                const widthPercent = (colSpan / chartLabels.length) * 100;
                                return (
                                    <div
                                        key={yl.year}
                                        style={{
                                            width: `${widthPercent}%`,
                                            textAlign: "center",
                                            position: "relative",
                                        }}
                                    >
                                        {yl.year}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            ) : (
                !loading && !hasPatternMatches && (
                    <div style={{ color: "#888", padding: "20px", textAlign: "center", fontSize: "1.05em" }}>
                        No chart data available for this period.
                    </div>
                )
            )}
            {hasPatternMatches && renderDataTable()}
        </div>
    );
};

export default Pattern_low_price_reco;